import * as React from 'react'
import './BarChartUI.scss';
import { IBaseInputFieldProperty, IPropertyEditorProps, IUIPropertyDefinition, IWDDesignModeProps, PropertyPath } from '../common';
import { PropertyContainer, PropertyGroup, PropertyGroupTitle } from '../ui-props/common';
import { FieldProperty, IFieldTitleProps } from '../components/FieldTitle';
import { useEffectWithPolling } from 'uxp/components';
import { IContextProvider } from '../../../uxp';
import { hasValue } from '../../../util';
import { ChartAxisLabelProperty, IChartAxisLabelProperty, extractAxisLabelProps } from '../ui-props/ChartAxisLabelProperty';
import { IMarginProperty, MarginProperty, extractMargins } from '../ui-props/MarginProperty';
import { ChartDataSortingProperty, IChartDataSortingProperty, SortConfig, extractChartDataSortConfig } from '../ui-props/ChartDataSortingProperty';
import { ChartItemProperty, IChartItem, IChartResult, findAvailableFields } from './ChartItemProperty';
import { IContextData } from '../../../hooks/useContextData';
import { Area, Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import { SimpleTextProperty } from '../ui-props/SimpleTextProperty';
import { BooleanProperty } from '../ui-props/BooleanProperty';
import { BooleanGroupProperty } from '../ui-props/BooleanGroupProperty';
import { SimpleNumberProperty } from '../ui-props/SimpleNumberProperty';
import { executeAndBind, getDependencyParameters, IDataSource, processInitialSource } from '../data-source-picker/datasource';

export interface IBarChartUIDefinition {


    datasources: IDataSource[];
    xLabel?: IChartAxisLabelProperty,
    yLabel?: IChartAxisLabelProperty,
    margins?: IMarginProperty,
    sortData: IChartDataSortingProperty,
    xField: string; // deperecated
    timeseries: boolean;
    refreshInterval: string;
    chartItems: IChartItem[];
    appearance: {
        showLegend: boolean;
        showGrid: boolean;
        showYAxis: boolean;
        stackBars: boolean;
    }
    filter?: any,
    legendTopMargin?: number
}
export interface IBarChartUIProps extends IBarChartUIDefinition {
    designer?: IWDDesignModeProps;
    uxpContext?: IContextProvider;
    contextData?: IContextData;
}
interface IBarChartUIProperty extends IBarChartUIDefinition {

}
export interface IBarChartUIFieldProperty extends IBaseInputFieldProperty, IBarChartUIDefinition {
    fieldLabel?: IFieldTitleProps;
}


export function BarChartUIProperty(title: string, props: IBarChartUIProperty): IUIPropertyDefinition<IBarChartUIFieldProperty> {
    return {
        defaults: {
            appearance: {
                showGrid: true,
                showLegend: false,
                showYAxis: true,
                stackBars: true,
            },
            chartItems: [],
            datasources: [],
            refreshInterval: '120000',
            sortData: {
                isTimeSeriesData: false,
                sortBy: 'none',
                sortOrder: 'asc'
            },
            timeseries: false,
            xField: '',

        },
        render: (props, uxpContext, contextData, containerId) => {
            return <BarChartUIPropertyEditor {...props} isNotField={true} uxpContext={uxpContext} contextData={contextData} />
        }
    };
}





export const BarChartUIPropertyEditor: React.FunctionComponent<IPropertyEditorProps<IBarChartUIFieldProperty> & { isNotField?: boolean }> = (props) => {
    const { uiProps } = props;
    const xValues: string[] = [];
    function makeProps<T>(item: keyof IBarChartUIFieldProperty): IPropertyEditorProps<T> {
        return {
            designer: props.designer,
            onChange: (newProps: T) => {
                (uiProps as any)[item] = newProps;
                // const np = {...uiProps,[item]:newProps};
                const np = uiProps;
                props.onChange(np);
            },
            onChangeAny: (path: PropertyPath, value: any) => {
                props.onChangeAny(path, value);
            },
            parentUIProps: props.parentUIProps,
            uiProps: uiProps[item],
            uxpContext: props.uxpContext,
            contextData: props.contextData,
            containerId: props.containerId,
        }
    }
    return <PropertyContainer>
        <PropertyGroup>
            <PropertyGroupTitle>Bar Chart</PropertyGroupTitle>
            {
                (props.isNotField != true) &&
                <FieldProperty uiProps={props.uiProps} onChange={props.onChange} />

            }
        </PropertyGroup>

        {SimpleTextProperty('Refresh Time', '60000').render(makeProps('refreshInterval'), props.uxpContext, props.contextData)}
        {BooleanProperty('Timeseries Data', false).render(makeProps('timeseries'), props.uxpContext, props.contextData)}
        {ChartAxisLabelProperty({ axis: 'x', field: '', showAllLabels: false, rotation: 0, rotateDirection: 'clockwise', label: { value: '', offset: 0 } }, () => xValues).render(makeProps('xLabel'), props.uxpContext, props.contextData)}
        {ChartAxisLabelProperty({ axis: 'y', rotation: 0, rotateDirection: 'clockwise', label: { value: '', offset: 0 }, showDecimals: false }, () => xValues).render(makeProps('yLabel'), props.uxpContext, props.contextData)}
        {MarginProperty({ top: 50, bottom: 50, left: 50, right: 50 }).render(makeProps('margins'), props.uxpContext, props.contextData)}
        {ChartDataSortingProperty({ sortBy: 'none', sortOrder: 'asc', isTimeSeriesData: uiProps.timeseries }).render(makeProps('sortData'), props.uxpContext, props.contextData)}
        {ChartItemProperty('Chart Elements', [], uiProps.datasources, props.uxpContext, () => props.contextData).render(makeProps('chartItems'), props.uxpContext, props.contextData)}
        {BooleanGroupProperty('Appearance', { showLegend: false, showGrid: false, showYAxis: false, stackBars: false, }, { showLegend: 'Show Legend', showGrid: 'Show Grid', showYAxis: 'Show Y-Axis', stackBars: 'Stack Bars' }).render(makeProps('appearance'), props.uxpContext, props.contextData)}
        {SimpleNumberProperty('Legend Top Margin', 20).render(makeProps('legendTopMargin'), props.uxpContext, props.contextData)}




    </PropertyContainer>;
}
const randomDataMap: { [key: string]: number[] } = {};
function generateRandomData(field: string) {
    if (!randomDataMap[field]) {
        let randomData: number[] = [];
        for (let i = 0; i < 10; i++) {
            randomData.push(25 + Math.random() * 75);
        }
        randomDataMap[field] = randomData;
    }
    return randomDataMap[field];
}

function intFmt(x: string) {
    return Intl.NumberFormat().format(Number(x));
}

function sortChartData(chartData: any[], sorting: SortConfig, xField: string) {
    if (!Array.isArray(chartData)) return []

    chartData = [...chartData]

    if (sorting.sortBy === 'none') {
        return chartData;
    }

    if (sorting.sortBy == 'label') {
        if (!xField) return chartData
        return chartData.sort((a, b) => {
            if (typeof a[xField] == 'string') {
                return sorting.sortOrder == 'asc' ? a[xField].localeCompare(b[xField]) : b[xField].localeCompare(a[xField])
            } else if (typeof a[xField] == 'number') {
                return sorting.sortOrder == 'asc' ? a[xField] - b[xField] : b[xField] - a[xField]
            } else {
                return sorting.sortOrder == 'asc' ?
                    a[xField] > b[xField] ? 1 : (a[xField] === b[xField] ? 0 : -1)
                    : b[xField] > a[xField] ? 1 : (b[xField] === a[xField] ? 0 : -1)
            }
        })
    }
    else {
        const valueKey = Object.keys(chartData[0] || {}).find(s => s?.startsWith('0/'))
        if (!valueKey) return chartData
        return chartData.sort((a, b) => (sorting.sortOrder == 'asc' ? a[valueKey] - b[valueKey] : b[valueKey] - a[valueKey]))
    }
}


export const BarChartUI: React.FunctionComponent<IBarChartUIProps> = (props) => {


    const uiProps = props;
    const uxpContext = props.uxpContext;
    const designer = props.designer;
    const contextData = props.contextData;


    let [chartSeries, setChartSeries] = React.useState<IChartResult[]>([]);
    const [loading, setLoading] = React.useState(false)

    const [IS, setIntitialSource] = React.useState(null);

    const containerRef = React.useRef(null)


    function addErrors(errors: any[]) {

    }
    function removeErrors(errors: string[]) {

    }
    function hasAtLeastOneChartElement(showError?: boolean): boolean {
        if (!uiProps?.chartItems?.length) {
            if (showError) {
                addErrors?.([{
                    id: 'no-chart-elements',
                    type: 'property',
                    error: 'Chart must contain at least one chart element',
                    containerId: 'bar-chart-container',
                    fieldId: 'chartItems'
                }])
            }

            return false
        }

        removeErrors?.(['no-chart-elements'])

        return true
    }

    function doesEveryChartElementContainADataSource(showError?: boolean): boolean {
        if (!uiProps?.chartItems?.every((element) => !!element.binding)) {
            if (showError) {
                addErrors?.([{
                    id: 'no-data-source',
                    type: 'property',
                    error: 'Every chart element must contain a data source',
                    containerId: 'bar-chart-container',
                    fieldClassName: 'no-data-source',
                }])
            }

            return false
        }
        removeErrors?.(['no-data-source'])
        return true
    }

    function validateBeforeSave() {
        if (!hasAtLeastOneChartElement(true)) return false
        if (!doesEveryChartElementContainADataSource(true)) return false
        return true
    }

    React.useEffect(() => {
        validateBeforeSave()
    }, [uiProps])


    // React.useImperativeHandle(ref, () => ({
    //     beforeSave: validateBeforeSave,
    //     onPreview: validateBeforeSave
    // }))

    React.useEffect(() => {
        processInitialSource(designer, uxpContext, uiProps, IS, 'dictionary-array')
            .then(res => {
                if (res) {
                    let { source } = res
                    let items = res.chartItems || []

                    items.push({
                        binding: source,
                        color: '#06f',
                        field: '',
                        label: '',
                        type: 'bar',
                        xTitle: '',
                        yTitle: '',
                    })

                    let newUIProps = Object.assign({}, { ...res }, { chartItems: items })
                    props.designer?.setUIProps(newUIProps)
                }
            })
            .catch(e => {
                console.log('exception ', e);

            })
    }, [IS])


    let xValues = React.useMemo(() => {
        let possibleValues: any = {};
        for (let item of chartSeries) {
            if (!item.type) {
                continue;
            }
            findAvailableFields(item.type, uiProps.datasources, possibleValues);
        }
        let pv = Object.keys(possibleValues);
        pv.sort();
        return pv;
    }, [chartSeries]);

    let interval = Number(uiProps?.refreshInterval) || 60000;

    // const { width, height, entry, unobserve, observe } = useDimensions({
    //     onResize: (p: any) => { },
    // });
    function renderLineSeries(result: IChartResult, index: number) {
        return <Line type={'monotone'} strokeWidth={2} yAxisId={result.type.field} dataKey={index + '/' + result.type.field} stroke={result.type.color} fill={result.type.color} name={result.type.label || ('Bar ' + index)} />
    }
    function renderAreaSeries(result: IChartResult, index: number) {
        return <Area fillOpacity={0.5} type={'monotone'} yAxisId={result.type.field} dataKey={index + '/' + result.type.field} fill={result.type.color} name={result.type.label || `Area ${index}`} />
    }
    function renderBarSeries(result: IChartResult, index: number) {
        return <Bar
            stackId={uiProps.appearance?.stackBars ? 'a' : null}
            radius={[2, 2, 0, 0]}
            maxBarSize={15}
            yAxisId={result.type.field}
            dataKey={index + '/' + result.type.field}
            fill={result.type.color}
            name={result.type.label || ('Line ' + index)}
        />
    }
    function renderChartSeries(result: IChartResult, index: number) {
        switch (result.type.type) {
            default:
            case 'line':
                return renderLineSeries(result, index);
            case "area":
                return renderAreaSeries(result, index);
            case "bar":
                return renderBarSeries(result, index);
        }
    }
    function formatXAxis(item: any) {
        if (!uiProps.timeseries) {
            return item;
        }
        return new Date(item).toLocaleString();
    }

    let xf = uiProps?.xLabel?.field || '__x';

    let deps: any[] = [];
    if (designer?.mode == 'preview' || designer?.mode == 'thumbnail') {
        deps = [uiProps.chartItems];
    }
    let sourceDeps = getDependencyParameters(uiProps.chartItems?.map(ci => ci.binding), uiProps.datasources, contextData);
    deps = [...deps, ...sourceDeps, JSON.stringify(contextData)];
    
    useEffectWithPolling(uxpContext, "chart/1", Number(interval) || 60000, async () => {
        if (designer?.mode == 'preview') {
            let results: IChartResult[] = [];
            for (let item of uiProps.chartItems || []) {
                let data = [];

                for (var i = 0; i < 10; i++) {
                    data.push({
                        [xf]: uiProps.timeseries ? (new Date(Number(new Date()) - (24 - i) * 3600 * 1000)) : (`Label ${i}`),
                        [item.field]: generateRandomData(item.field)[i],
                    });
                }
                results.push({ data, type: item });
            }
            setChartSeries(results);
            return;
        }
        if (designer?.mode != 'thumbnail') {
            let results: IChartResult[] = [];
            for (let item of uiProps.chartItems) {
                let params: any = { filters: contextData?.filters || {} }

                try {
                    let data = await executeAndBind(uxpContext, item.binding, uiProps.datasources, params, contextData);
                    results.push({ data, type: item });
                }
                catch (e) {
                    console.log('error getting data', e)
                }
            }
            setChartSeries(results);
        }
    }, deps);
    let chartData: any[] = [];
    let uniqueFields: any = {};
    if (chartSeries.length == 0 && (designer?.mode == 'thumbnail' || designer?.mode == 'preview')) {
        xf = 'x';
        console.log('HERE WE GO YET');
        chartSeries = [
            {
                data: generateRandomData('nodata').map((y, x) => ({ x, y })),
                type: {
                    binding: { path: '', source: '' },
                    color: 'rgba(127,127,127,0.5)',
                    field: 'y',
                    label: 'Data',
                    type: 'bar',
                    xTitle: '',
                    yTitle: '',
                    unit: ''
                }
            }
        ]
    }
    for (let sri in chartSeries) {
        let sr = chartSeries[sri];
        uniqueFields[sr.type.field] = 1;
        let data = sr.data;
        for (let row of data) {
            let found = chartData.find(x => x[xf] == row[xf]);
            if (!found) {
                found = { [xf]: row[xf] };
                chartData.push(found);
            }
            found[sri + '/' + sr.type.field] = Number(row[sr.type.field]);
        }
    }


    let fields = Object.keys(uniqueFields);
    let showLegend = !!uiProps.appearance?.showLegend;

    const xLabelProps = extractAxisLabelProps(uiProps.xLabel)
    const yLabelProps = extractAxisLabelProps(uiProps?.yLabel)
    const margins = extractMargins(uiProps.margins)
    const sortConfig = extractChartDataSortConfig(uiProps.sortData)

    console.log('chartdata', xLabelProps, yLabelProps)

    const sortedChartData = React.useMemo(() => {
        const sorted = sortChartData(chartData, sortConfig, xf)

        return sorted
    }, [chartData, sortConfig])

    let unitField = uiProps.chartItems[0]?.unit;
    let unit = chartSeries[0]?.data[0]?.[unitField] || '';

    const CustomTooltip = ({ payload, label }: any) => {
        let labelOnTooltip = label;
        if (uiProps.timeseries) {
            labelOnTooltip = new Date(label).toLocaleString();
        }
        return (
            <div style={{ backgroundColor: 'rgb(255, 255, 255)', border: '1px solid rgb(204, 204, 204)', padding: '0 10px'}}>
                <p style={{padding: '0'}}>{labelOnTooltip}</p>
                {payload?.map((p: any) => <p style={{color: p?.stroke, padding: '0'}}>{p?.name}: {intFmt(Number(p?.value).toFixed(2))} {unit}</p>)}
            </div>
        );
    };

    return <div style={{ height: '300px' }}>
        <ResponsiveContainer>
            <ComposedChart
                barCategoryGap={2}
                barSize={15}
                data={sortedChartData}
                margin={margins}
            >

                <Tooltip content={<CustomTooltip />} />

                {showLegend &&
                    <Legend align="center" verticalAlign="bottom"
                        wrapperStyle={{ paddingTop: hasValue(uiProps.legendTopMargin, true) ? uiProps.legendTopMargin : 20 }}
                    />
                }

                {uiProps.appearance?.showGrid &&
                    <CartesianGrid strokeWidth={1} horizontal={true} vertical={false} strokeOpacity={0.5} />
                }

                <XAxis
                    dataKey={xf}
                    tickFormatter={formatXAxis}
                    {...xLabelProps}
                />
                {
                    fields.map((f, i) => <YAxis
                        orientation={(i == 0) ? 'left' : 'right'}
                        hide={i > 1 || !uiProps.appearance?.showYAxis}
                        yAxisId={f}
                        {...yLabelProps}
                        textAnchor='end'
                    />)
                }

                {
                    chartSeries.map((r, i) => renderChartSeries(r, i))
                }

            </ComposedChart>
        </ResponsiveContainer>
    </div>;

}